import { useFeatureFlags } from "@hooks/http/use-feature-flag";

import { SetupSection } from "./components/setup-section";
import { Code, OrderedList, SubSubSectionHeader } from "./components/styled";
import { SubmitTicketButton } from "./components/submit-ticket-button";
import { type PlatformDetails } from "./platform-details";

export const MNTNDetails: PlatformDetails = {
  northbeamDocUrl: "https://docs.northbeam.io/docs/tracking-for-mntn",
  officialDocUrl: "",
  additionalDetails: [
    {
      title: "What does the integration do?",
      content:
        "We pull spend and impressions metrics from MNTN, which enables our view-through modeling data. MNTN only tracks connected TV, not linear.",
    },
  ],
};

export const MNTNSetup = ({
  connectionContent,
}: {
  connectionContent: React.ReactNode;
}) => {
  const { hasFeatureFlag } = useFeatureFlags();

  const displayMntnConnectionCard = hasFeatureFlag("enableMNTNConnection");
  if (!displayMntnConnectionCard) {
    return <MNTNManualSetup />;
  }

  return (
    <>
      <SetupSection title="Define the list of channels you want to connect to Northbeam">
        <span className="text-sm leading-tight">
          Some channels have limited attributions in Northbeam. <br />
          For that reason, define the list of channels you intend to connect to
          validate their current attribution.
        </span>
      </SetupSection>
      <SetupSection title="Generate an MNTN API key">
        <SubSubSectionHeader>
          Make sure you’ve followed these steps:
        </SubSubSectionHeader>
        <OrderedList variant="upper-alpha">
          <>Log into your MNTN account</>
          <>Click on the menu in the top, right corner</>
          <>Click on ’My Account’</>
          <>Click on ’API’</>
        </OrderedList>
      </SetupSection>
      <SetupSection title="Connect your MNTN Account">
        {connectionContent}
      </SetupSection>
    </>
  );
};

const MNTNManualSetup = () => (
  <>
    <SetupSection title="Locate your Northbeam Client ID, Website URL, and Collector Subdomain">
      <p className="text-sm leading-tight">
        Before starting, it's important to have the following information ready
        for fastest and easiest integration setup.
      </p>
      <OrderedList variant="upper-alpha" className="space-y-6 font-semibold">
        <>
          Locate your Northbeam Client ID
          <OrderedList indent={1} className="font-normal">
            <>Log into Northbeam</>
            <>
              Find your Northbeam Client ID in the your browser’s URL slug
              <div className="flex gap-2 mt-2">
                <i>Example:</i>
                <Code>
                  <span className="text-interstellar">
                    dashboard.northbeam.io/
                  </span>
                  87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7
                </Code>
              </div>
            </>
          </OrderedList>
        </>
        <>
          Locate your Website URL
          <OrderedList indent={1} className="font-normal">
            <>Log into Northbeam</>
            <>Navigate to ’Settings’</>
            <>
              Find it in ’Profile’, under ’Domain Name’{" "}
              <div className="flex gap-2 mt-2">
                <i>Example:</i>
                <Code>www.apple.com</Code>
              </div>
            </>
          </OrderedList>
        </>
        <>
          Locate your ‘Collector Subdomain’
          <OrderedList indent={1} className="font-normal">
            <>Log into Northbeam</>
            <>Navigate to ’Settings’</>
            <>Navigate to ‘Setup DNS Entry’</>
            <>
              Find the subdomain listed (e.g. i, i2, i3, or i4.){" "}
              <div className="flex gap-2 mt-2">
                <i>Example:</i>
                <Code>i</Code>
              </div>
            </>
          </OrderedList>
        </>
      </OrderedList>
    </SetupSection>
    <SetupSection title="Insert impression tracking URL">
      <p className="mb-6 text-sm leading-tight">
        In order for us to fetch impressions, we need a specific Impression
        Tracking URL to be placed. Every customer has a unique URL -- based on
        the information above.
      </p>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <SubSubSectionHeader>
            Make sure you’ve followed these steps:
          </SubSubSectionHeader>
          <OrderedList variant="upper-alpha">
            <>Log into MNTN</>
            <>Click on the menu in the top, right corner</>
            <>Click on ’My Account’</>
            <>Click on ’Creative Tracking’ in the left side menu</>
            <>Click on ’Default Impression Tracking’</>
            <>Enter URL in ’Default Macro-Enabled Impression Tracking’</>
            <>Click ’Save Changes’</>
          </OrderedList>
        </div>
        <div className="flex flex-col gap-2">
          <SubSubSectionHeader>
            Impression tracking URL format
          </SubSubSectionHeader>
          <Code className="text-sm leading-tight" textColor="text-interstellar">
            https://<code className="text-venus">[subdomain]</code>.
            <code className="text-venus">[website-url]</code>/i?aid=
            <code className="text-venus">[client-id]</code>
            &e=se&se_ca=northbeam&se_ac=impression&se_la=mntn&se_pr=$
            {"{aid}"}%7C%3A%7C${"{cagid}"}%7C%3A%7C${"{cgid}"}%7C%3A%7C $
            {"{device_id}"}
            %7C%3A%7C${"{ip}"}%7C%3A%7C${"{useragent}"}%7C%3A%7C${"{random}"}
            %7C%3A%7C${"{timestamp}"}
          </Code>
        </div>
        <div className="flex flex-col gap-2">
          <SubSubSectionHeader>Example URL</SubSubSectionHeader>
          <Code className="text-sm leading-tight">
            https://i.northbeam.io/i?aid=925160f8-ecc3-4457-8c3e-b75fe5dbf0ac&e=se&se_ca=northbeam&se_ac=impression&se_la=mntn&se_pr=$
            {"{aid}"}%7C%3A%7C${"{cagid}"}%7C%3A%7C${"{cgid}"}%7C%3A%7C $
            {"{device_id}"}
            %7C%3A%7C${"{ip}"}%7C%3A%7C${"{useragent}"}%7C%3A%7C${"{random}"}
            %7C%3A%7C${"{timestamp}"}
          </Code>
        </div>
      </div>
    </SetupSection>
    <SetupSection title="Generate an MNTN API key">
      <SubSubSectionHeader>
        Make sure you’ve followed these steps:
      </SubSubSectionHeader>
      <OrderedList variant="upper-alpha">
        <>Log into your MNTN account</>
        <>Click on the menu in the top, right corner</>
        <>Click on ’My Account’</>
        <>Click on ’API’</>
      </OrderedList>
    </SetupSection>
    <SetupSection title="Share your ‘MNTN API Key’ with us and let us know ‘Impression Tracking URLs’ are set up.">
      <p>
        Submit a ticket to share your ‘MNTN API Key’ and notify us that your
        ‘Impression Tracking URLs’ are set up.
      </p>
      <SubmitTicketButton />
    </SetupSection>
  </>
);
